/**
 * Сахар над `useAuthStore()` для шаблонов.
 *
 * Существует ради одной строчки — `storeToRefs`. Прямое `const { user } =
 * useAuthStore()` молча ломает реактивность (деструктуризация снимает `.value`
 * и связь с сторой теряется), и это самая частая ошибка в Pinia. Здесь она
 * сделана один раз и правильно, а вьюхи пишут `const { isAdmin } = useAuth()`.
 *
 * Стор при этом отдаётся наружу как `store` — действия (`login`, `logout`)
 * зовут через него: `storeToRefs` функции не оборачивает, и раскладывать их
 * по отдельным полям значило бы держать два списка вместо одного.
 */
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'

export function useAuth() {
  const store = useAuthStore()

  const {
    user,
    role,
    isAdmin,
    isAuthenticated,
    permissions,
    displayName,
    initials,
  } = storeToRefs(store)

  /**
   * Проверка по **списку прав из `GetMe`**, а не по роли — сервер шлёт
   * `permissions` именно за этим (§6 `types/api.ts`). Значения — в `PERMISSIONS`.
   *
   * ⚠️ Это аффорданс: скрыть кнопку, которую всё равно запретят. Авторитет —
   * Go-бэкенд, он вернёт 403 независимо от того, что решил фронт.
   *
   * Обёртка, а не `store.can` напрямую: так вызов всегда идёт в актуальную
   * стору и переживёт любую реализацию F3-E.
   */
  function can(permission: string): boolean {
    return store.can(permission)
  }

  return {
    user,
    role,
    isAdmin,
    isAuthenticated,
    permissions,
    displayName,
    initials,
    can,
    store,
  }
}
